import { useTranslation } from "react-i18next";

import { Gradients } from "components/gradients";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { formatPercentage } from "utils/formatters";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import {
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 225;
const END_ANGLE = 315;
const MAX_VALUE = 1.0;
const DIAMETER = 450;
const ARC_WIDTH = 30;
const STROKE_CAP = "round";

export const QualityGauge = () => {
  const { t } = useTranslation();

  // Store values
  const safetyQuality = useStore((state) => state.safetyQuality);

  // Animated value for smooth transitions
  const animatedSafetyQuality = useAnimatedValue(safetyQuality);

  // Gauge setup
  const gauge = useGauge({
    domain: [0, MAX_VALUE],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: 18,
    diameter: DIAMETER,
  });

  const label = t("dashboard.safety_quality");
  const value = safetyQuality;
  const limit = 0.75;
  const limitAngle = gauge.valueToAngle(MAX_VALUE - limit);
  const isAboveLimit = value !== null && value < limit;

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 2)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle:
              END_ANGLE +
              START_ANGLE -
              gauge.valueToAngle(animatedSafetyQuality),
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={`url(#${
            isAboveLimit ? SvgGradients.Red : SvgGradients.Blue
          })`}
          strokeWidth={value !== null && value > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-value"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.1,
            startAngle: limitAngle,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={
            isAboveLimit
              ? `url(#${SvgGradients.Red})`
              : `url(#${SvgGradients.RedFaded})`
          }
          strokeWidth={ARC_WIDTH * 0.3}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          x={"48%"}
          fill={isAboveLimit ? `url(#${SvgGradients.Red})` : Color.White}
          className="text-[4.5rem] font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="end"
        >
          {value !== null ? `${formatPercentage(value)}%` : PLACEHOLDER}
        </text>
        <text
          x={"46%"}
          y={"13%"}
          className="text-2xl font-semibold"
          fill={isAboveLimit ? Color.Rose[600] : Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="end"
        >
          {label}
        </text>
      </g>
    </svg>
  );
};
